import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { getCashInbox } from "../../../../services/cashBookApi";
import { T, useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import type { CashInbox } from "../types";

/** "To check: 3 sheets · 2 photos missing · 1 change to decide · 1 handover" (V1_SPEC 11.6 O1, D34). */
export const toCheckLine = (s: CashInbox["summary"], t: T): string =>
  [
    s.sheets ? t(s.sheets === 1 ? "1 sheet" : "{{n}} sheets", { n: s.sheets }) : "",
    s.spends ? t(s.spends === 1 ? "1 spends page" : "{{n}} spends pages", { n: s.spends }) : "",
    s.missing ? t(s.missing === 1 ? "1 photo missing" : "{{n}} photos missing", { n: s.missing }) : "",
    s.changes ? t(s.changes === 1 ? "1 change to decide" : "{{n}} changes to decide", { n: s.changes }) : "",
    s.transfers ? t(s.transfers === 1 ? "1 handover" : "{{n}} handovers", { n: s.transfers }) : "",
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * The owner's and manager's alert on Karkhana home, only with the Cash book add-on and something to check.
 * One inbox call for the summary, made only when the status badge says there is something.
 */
const CashToCheckAlert = () => {
  const { t } = useKT();
  const { enabled, badge } = useCashbookStatus();
  const toCheck = enabled ? badge.toCheck : 0;
  const [summary, setSummary] = useState<CashInbox["summary"] | null>(null);

  useEffect(() => {
    if (!toCheck) {
      setSummary(null);
      return;
    }
    let live = true;
    getCashInbox(14)
      .then((res) => live && setSummary(res?.success ? res.data?.summary || null : null))
      .catch(() => live && setSummary(null));
    return () => {
      live = false;
    };
  }, [toCheck]);

  if (!toCheck) return null;
  const line = summary ? toCheckLine(summary, t) : "";
  return (
    <Link to={kp.cashBook()} className="kh-alert is-warn">
      <span>
        <strong>{t("To check")}:</strong> {line || t(toCheck === 1 ? "1 item" : "{{n}} items", { n: toCheck })}
      </span>
      <span className="kh-alert__btn">{t("Open")}</span>
    </Link>
  );
};

export default CashToCheckAlert;
